import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  onAuthStateChanged,
  User as FirebaseUser,
  updateProfile,
  sendPasswordResetEmail,
  signInAnonymously as firebaseSignInAnonymously,
} from 'firebase/auth';
import { doc, setDoc, getDoc, query, collection, where, getDocs, limit } from 'firebase/firestore';
import { auth, db } from './config';
import { User, Organization, AuthUser, OrgMember } from '../types/models';
import { v4 as uuidv4 } from 'uuid';

function generateInviteCode(): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // no I/O/0/1 to avoid confusion
  let code = '';
  for (let i = 0; i < 6; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
}

export class AuthService {
  // Sign in anonymously (guest demo)
  static async signInAnonymously(): Promise<{ authUser: AuthUser; user: User }> {
    const userCredential = await firebaseSignInAnonymously(auth);
    const firebaseUser = userCredential.user;

    const authUser: AuthUser = {
      uid: firebaseUser.uid,
      email: null,
      displayName: 'Guest',
      emailVerified: false,
    };

    const user: User = {
      userID: firebaseUser.uid,
      userEmail: '',
      firstName: 'Guest',
      lastName: '',
      dateCreatedUnix: Date.now() / 1000,
      dateCreatedString: new Date().toLocaleDateString(),
      fcmToken: '',
      adminStatus: 0,
      joinedChatConversations: [],
    };

    return { authUser, user };
  }

  // Sign in with Google
  static async signInWithGoogle(): Promise<AuthUser> {
    const provider = new GoogleAuthProvider();
    const userCredential = await signInWithPopup(auth, provider);
    return {
      uid: userCredential.user.uid,
      email: userCredential.user.email,
      displayName: userCredential.user.displayName,
      emailVerified: userCredential.user.emailVerified,
    };
  }

  // Sign in with email and password
  static async signIn(email: string, password: string): Promise<AuthUser> {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    return {
      uid: userCredential.user.uid,
      email: userCredential.user.email,
      displayName: userCredential.user.displayName,
      emailVerified: userCredential.user.emailVerified,
    };
  }

  // Sign up with email/password, creates a personal organization automatically
  static async signUp(
    email: string,
    password: string,
    displayName: string
  ): Promise<{ authUser: AuthUser; user: User; organization: Organization }> {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const firebaseUser = userCredential.user;

    await updateProfile(firebaseUser, { displayName });

    // Generate unique invite code for the personal org
    let inviteCode = generateInviteCode();
    let existing = await this.findOrgByInviteCode(inviteCode);
    while (existing) {
      inviteCode = generateInviteCode();
      existing = await this.findOrgByInviteCode(inviteCode);
    }

    const organizationId = uuidv4();
    const organization: Organization = {
      id: organizationId,
      name: `${displayName}'s Organization`,
      adminEmails: [email],
      inviteCode,
      createdAt: new Date(),
      settings: {
        allowGuestCheckouts: true,
        requireApprovalForCheckouts: false,
      },
    };

    await setDoc(doc(db, 'organizations', organizationId), organization);

    const firstName = displayName.split(' ')[0] || displayName;
    const lastName = displayName.split(' ').slice(1).join(' ') || '';

    const user: User = {
      userID: firebaseUser.uid,
      userEmail: email,
      firstName,
      lastName,
      dateCreatedUnix: Date.now() / 1000,
      dateCreatedString: new Date().toLocaleDateString(),
      fcmToken: '',
      adminStatus: 1,
      role: 'admin',
      memberStatus: 'active',
      joinedChatConversations: [],
      organizationID: organizationId,
    };

    await setDoc(doc(db, 'users', firebaseUser.uid), user);

    // Create admin member doc
    const member: OrgMember = {
      id: firebaseUser.uid,
      email,
      displayName,
      role: 'admin',
      memberStatus: 'active',
      joinedAt: Date.now() / 1000,
    };
    await setDoc(
      doc(db, 'organizations', organizationId, 'members', firebaseUser.uid),
      member
    );

    const authUser: AuthUser = {
      uid: firebaseUser.uid,
      email: firebaseUser.email,
      displayName: firebaseUser.displayName,
      emailVerified: firebaseUser.emailVerified,
    };

    return { authUser, user, organization };
  }

  // Join an existing organization via invite code (current user becomes pending member)
  static async joinOrganization(
    inviteCode: string
  ): Promise<Organization> {
    const firebaseUser = auth.currentUser;
    if (!firebaseUser) throw new Error('You must be signed in to join an organization.');

    const organization = await this.findOrgByInviteCode(inviteCode);
    if (!organization) {
      throw new Error('Invalid invite code. Please check and try again.');
    }

    // Create pending member doc in target org
    const member: OrgMember = {
      id: firebaseUser.uid,
      email: firebaseUser.email || '',
      displayName: firebaseUser.displayName || '',
      role: 'viewer',
      memberStatus: 'pending',
      joinedAt: Date.now() / 1000,
    };
    await setDoc(
      doc(db, 'organizations', organization.id, 'members', firebaseUser.uid),
      member
    );

    // Update user doc to point to new org
    await setDoc(doc(db, 'users', firebaseUser.uid), {
      organizationID: organization.id,
      role: 'viewer',
      memberStatus: 'pending',
    }, { merge: true });

    return organization;
  }

  // Create new organization account
  static async createOrganizationAccount(
    email: string,
    password: string,
    organizationName: string,
    adminName: string
  ): Promise<{ authUser: AuthUser; user: User; organization: Organization }> {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const firebaseUser = userCredential.user;

    await updateProfile(firebaseUser, { displayName: adminName });

    // Generate unique invite code
    let inviteCode = generateInviteCode();
    // Check for collision (extremely unlikely but safe)
    let existing = await this.findOrgByInviteCode(inviteCode);
    while (existing) {
      inviteCode = generateInviteCode();
      existing = await this.findOrgByInviteCode(inviteCode);
    }

    const organizationId = uuidv4();
    const organization: Organization = {
      id: organizationId,
      name: organizationName,
      adminEmails: [email],
      inviteCode,
      createdAt: new Date(),
      settings: {
        allowGuestCheckouts: true,
        requireApprovalForCheckouts: false,
      },
    };

    await setDoc(doc(db, 'organizations', organizationId), organization);

    const firstName = adminName.split(' ')[0] || adminName;
    const lastName = adminName.split(' ').slice(1).join(' ') || '';

    const user: User = {
      userID: firebaseUser.uid,
      userEmail: email,
      firstName,
      lastName,
      dateCreatedUnix: Date.now() / 1000,
      dateCreatedString: new Date().toLocaleDateString(),
      fcmToken: '',
      adminStatus: 1,
      role: 'admin',
      memberStatus: 'active',
      joinedChatConversations: [],
      organizationID: organizationId,
    };

    await setDoc(doc(db, 'users', firebaseUser.uid), user);

    // Create admin member doc
    const member: OrgMember = {
      id: firebaseUser.uid,
      email,
      displayName: adminName,
      role: 'admin',
      memberStatus: 'active',
      joinedAt: Date.now() / 1000,
    };
    await setDoc(
      doc(db, 'organizations', organizationId, 'members', firebaseUser.uid),
      member
    );

    const authUser: AuthUser = {
      uid: firebaseUser.uid,
      email: firebaseUser.email,
      displayName: firebaseUser.displayName,
      emailVerified: firebaseUser.emailVerified,
    };

    return { authUser, user, organization };
  }

  // Find organization by invite code
  static async findOrgByInviteCode(code: string): Promise<Organization | null> {
    const q = query(
      collection(db, 'organizations'),
      where('inviteCode', '==', code.toUpperCase()),
      limit(1)
    );
    const snapshot = await getDocs(q);
    if (snapshot.empty) return null;
    return snapshot.docs[0].data() as Organization;
  }

  // Regenerate invite code for an organization
  static async regenerateInviteCode(orgId: string): Promise<string> {
    let newCode = generateInviteCode();
    let existing = await this.findOrgByInviteCode(newCode);
    while (existing) {
      newCode = generateInviteCode();
      existing = await this.findOrgByInviteCode(newCode);
    }
    await setDoc(doc(db, 'organizations', orgId), { inviteCode: newCode }, { merge: true });
    return newCode;
  }

  // Update user display name
  static async updateUserName(userId: string, firstName: string, lastName: string): Promise<void> {
    await setDoc(doc(db, 'users', userId), { firstName, lastName }, { merge: true });
    // Also update Firebase Auth profile if current user
    const firebaseUser = auth.currentUser;
    if (firebaseUser && firebaseUser.uid === userId) {
      await updateProfile(firebaseUser, { displayName: `${firstName} ${lastName}`.trim() });
    }
  }

  // Send password reset email
  static async resetPassword(email: string): Promise<void> {
    await sendPasswordResetEmail(auth, email);
  }

  // Sign out
  static async signOut(): Promise<void> {
    await signOut(auth);
  }

  // Get current user document from Firestore
  // Checks Firebase Auth UID first, then falls back to localStorage UUID (legacy)
  static async getCurrentUser(): Promise<User | null> {
    const firebaseUser = auth.currentUser;
    if (!firebaseUser) return null;

    // Anonymous users don't get persisted or an org
    if (firebaseUser.isAnonymous) {
      return {
        userID: firebaseUser.uid,
        userEmail: '',
        firstName: 'Guest',
        lastName: '',
        dateCreatedUnix: Date.now() / 1000,
        dateCreatedString: new Date().toLocaleDateString(),
        fcmToken: '',
        adminStatus: 0,
        joinedChatConversations: [],
      };
    }

    // Try Firebase Auth UID first (new accounts)
    const uidDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
    if (uidDoc.exists()) {
      return uidDoc.data() as User;
    }

    // Fall back to localStorage UUID (legacy/shared accounts)
    const legacyId = localStorage.getItem('MySavedUserID');
    if (legacyId) {
      const legacyDoc = await getDoc(doc(db, 'users', legacyId));
      if (legacyDoc.exists()) {
        return legacyDoc.data() as User;
      }
    }

    // Create new user document for Google users
    const names = firebaseUser.displayName?.split(' ') || ['User'];
    const firstName = names[0] || 'User';
    const lastName = names.slice(1).join(' ') || '';
    const displayName = firebaseUser.displayName || firstName;

    // Google users get a personal org (same as email signup)
    let inviteCode = generateInviteCode();
    let existing = await this.findOrgByInviteCode(inviteCode);
    while (existing) {
      inviteCode = generateInviteCode();
      existing = await this.findOrgByInviteCode(inviteCode);
    }

    const organizationId = uuidv4();
    const organization: Organization = {
      id: organizationId,
      name: `${displayName}'s Organization`,
      adminEmails: [firebaseUser.email || ''],
      inviteCode,
      createdAt: new Date(),
      settings: {
        allowGuestCheckouts: true,
        requireApprovalForCheckouts: false,
      },
    };
    await setDoc(doc(db, 'organizations', organizationId), organization);

    const newUser: User = {
      userID: firebaseUser.uid,
      userEmail: firebaseUser.email || '',
      firstName,
      lastName,
      dateCreatedUnix: Date.now() / 1000,
      dateCreatedString: new Date().toLocaleDateString(),
      fcmToken: '',
      adminStatus: 1,
      role: 'admin',
      memberStatus: 'active',
      joinedChatConversations: [],
      organizationID: organizationId,
    };
    await setDoc(doc(db, 'users', firebaseUser.uid), newUser);

    // Create admin member doc
    const member: OrgMember = {
      id: firebaseUser.uid,
      email: firebaseUser.email || '',
      displayName,
      role: 'admin',
      memberStatus: 'active',
      joinedAt: Date.now() / 1000,
    };
    await setDoc(
      doc(db, 'organizations', organizationId, 'members', firebaseUser.uid),
      member
    );

    return newUser;
  }

  // Get or create user ID (legacy, matches iOS app logic)
  private static getOrCreateUserID(): string {
    let userID = localStorage.getItem('MySavedUserID');
    if (!userID) {
      userID = uuidv4();
      localStorage.setItem('MySavedUserID', userID);
    }
    return userID;
  }

  // Auth state listener
  static onAuthStateChange(callback: (user: FirebaseUser | null) => void) {
    return onAuthStateChanged(auth, callback);
  }

  // Get user's organization
  static async getUserOrganization(userId: string): Promise<Organization | null> {
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (!userDoc.exists()) return null;

    const userData = userDoc.data() as User;
    if (!userData.organizationID) return null;

    const orgDoc = await getDoc(doc(db, 'organizations', userData.organizationID));
    if (!orgDoc.exists()) return null;

    return orgDoc.data() as Organization;
  }

  // Check if user is admin (supports both legacy adminStatus and new role field)
  static async isUserAdmin(userId: string): Promise<boolean> {
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (!userDoc.exists()) return false;

    const userData = userDoc.data() as User;
    return userData.role === 'admin' || userData.adminStatus === 1;
  }
}
